import { Injectable, signal, computed, inject, effect } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, firstValueFrom, of } from 'rxjs';

import {
  ToolDataModuleDataContractsToolItemDto,
  ToolDataModuleDataContractsToolItemTechnicalParameterValueDto,
  ToolDataModuleDataContractsToolItemClassificationClassGroupDto,
  ToolDataModuleDataContractsDeclaredValueType,
} from '@tfs/tool-library-data-api';

import { CrossTabChannel } from './cross-tab-channel';
import { ToolComparisonDataService } from './data-access/tool-comparison-data.service';
import { ToolItemEventsService } from './tool-item-events.service';

export type ToolItem = ToolDataModuleDataContractsToolItemDto;
type TechnicalParameter = ToolDataModuleDataContractsToolItemTechnicalParameterValueDto;
type ClassificationDto = ToolDataModuleDataContractsToolItemClassificationClassGroupDto;

export type ComparisonGroupName =
  'Basic information' | 'Classification' | 'Supplier information' | 'Technical parameters';

export interface ComparisonRowGroup {
  groupName: ComparisonGroupName;
  parameters: ComparisonParameter[];
}

export interface ComparisonParameter {
  name: string;
  displayName: string;
  description?: string;
  group: ComparisonGroupName;
  values?: Record<string, string | number | boolean | null>;
}

@Injectable({
  providedIn: 'root',
})
export class ToolComparisonService {
  private readonly dataService = inject(ToolComparisonDataService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly eventService = inject(ToolItemEventsService);
  private readonly maxItems = 4;
  private readonly storageKey = 'tool-comparison-item-ids';

  private readonly selectedItemIds = signal<string[]>([]);
  private readonly loadedItems = signal<(ToolItem | null)[]>([]);
  private readonly isLoadingItems = signal(false);

  // Broadcasts the selected item ids so other browser tabs stay in sync.
  private readonly idsChannel = new CrossTabChannel<string[]>('tool-comparison-ids', ids =>
    this.applyRemoteIds(ids)
  );

  readonly itemIds = this.selectedItemIds.asReadonly();
  readonly items = computed(() => this.padToMaxSlots(this.loadedItems()));
  readonly isLoading = this.isLoadingItems.asReadonly();

  readonly itemCount = computed(() => this.countFilledSlots(this.selectedItemIds()));
  readonly canAddMore = computed(() => this.itemCount() < this.maxItems);

  constructor() {
    this.loadFromStorage();

    effect(() => {
      const itemIds = this.selectedItemIds();
      this.loadItemsData(itemIds);
    });

    // Watch for item changes - refetch if in comparison
    this.eventService.itemChanged$.subscribe(changedItemId => {
      if (this.isItemInComparison(changedItemId)) {
        this.loadItemsData(this.selectedItemIds());
      }
    });

    // Watch for item deletions - remove if in comparison
    this.eventService.itemDeleted$.subscribe(deletedItemId => {
      if (this.isItemInComparison(deletedItemId)) {
        this.removeItemWithoutPersisting(deletedItemId);
      }
    });
  }

  /**
   * Attempts to add an item to comparison.
   * @returns true if added successfully, false if comparison is full or item already exists
   */
  tryAddItem(itemId: string): boolean {
    if (this.countFilledSlots(this.selectedItemIds()) >= this.maxItems) {
      this.snackBar.open(`Maximum of ${this.maxItems} items can be compared`, 'OK', {
        duration: 4000,
        panelClass: 'warning',
      });
      return false;
    }

    if (this.isItemInComparison(itemId)) {
      this.snackBar.open('This item is already in the comparison', 'OK', {
        duration: 4000,
        panelClass: 'warning',
      });
      return false;
    }

    this.selectedItemIds.update(items => {
      const newItems = [...items];
      const firstEmptyIndex = newItems.findIndex(id => this.isEmptySlot(id));

      if (firstEmptyIndex !== -1) {
        newItems[firstEmptyIndex] = itemId;
      } else if (newItems.length < this.maxItems) {
        newItems.push(itemId);
      }

      return newItems;
    });

    this.saveToStorage();
    return true;
  }

  removeItem(itemId: string): void {
    this.selectedItemIds.update(items => items.filter(id => id !== itemId));
    this.saveToStorage();
  }

  removeItemAtIndex(index: number): void {
    if (index < 0 || index >= this.maxItems) return;

    this.selectedItemIds.update(items => {
      const newItems = [...items];
      if (index < newItems.length) {
        newItems[index] = '';
      }
      return newItems;
    });
    this.saveToStorage();
  }

  replaceItem(index: number, itemId: string): void {
    if (index < 0 || index >= this.maxItems) return;

    this.selectedItemIds.update(items => {
      const newItems = [...items];
      while (newItems.length <= index) {
        newItems.push('');
      }
      newItems[index] = itemId;
      return newItems;
    });
    this.saveToStorage();
  }

  isItemInComparison(itemId: string): boolean {
    return this.selectedItemIds().includes(itemId);
  }

  getComparisonData(): Observable<ComparisonRowGroup[]> {
    const loadedValidItems = this.loadedItems().filter((item): item is ToolItem => item !== null);
    if (loadedValidItems.length === 0) return of([]);
    return of(this.buildComparisonData(loadedValidItems));
  }

  // ════════════════════════════════════════════════════════════════════════════════
  // Private - Data Loading
  // ════════════════════════════════════════════════════════════════════════════════

  /**
   * Loads item data from API. Items that return 401/404 are automatically removed.
   */
  private async loadItemsData(itemIds: string[]): Promise<void> {
    try {
      this.isLoadingItems.set(true);

      if (itemIds.length === 0) {
        this.loadedItems.set([]);
        this.isLoadingItems.set(false);
        return;
      }

      const originalItemCount = this.countFilledSlots(itemIds);
      let removedItems = false;

      const loadPromises = itemIds.map(async id => {
        if (this.isEmptySlot(id)) return null;

        try {
          return await firstValueFrom(this.dataService.loadItem(id));
        } catch (error: unknown) {
          if (this.isHttpError(error) && (error.status === 401 || error.status === 404)) {
            console.warn(`Access denied for item ${id}. Removing from comparison.`);
            this.removeItemWithoutPersisting(id);
            removedItems = true;
            return null;
          }
          console.warn(`Failed to load item ${id}:`, error);
          return null;
        }
      });

      const items = await Promise.all(loadPromises);
      this.loadedItems.set(items);

      if (removedItems && this.countFilledSlots(this.selectedItemIds()) !== originalItemCount) {
        this.saveToStorage();
      }
    } catch (error) {
      console.error('Failed to load comparison items:', error);
      this.loadedItems.set([]);
    } finally {
      this.isLoadingItems.set(false);
    }
  }

  // ════════════════════════════════════════════════════════════════════════════════
  //  Comparison Data Building
  // ════════════════════════════════════════════════════════════════════════════════

  private buildComparisonData(items: ToolItem[]): ComparisonRowGroup[] {
    return [
      this.buildBasicInfoGroup(items),
      this.buildClassificationGroup(items),
      this.buildSupplierGroup(items),
      this.buildTechnicalParametersGroup(items),
    ].filter(group => group.parameters.length > 0);
  }

  private buildBasicInfoGroup(items: ToolItem[]): ComparisonRowGroup {
    return {
      groupName: 'Basic information',
      parameters: [
        this.buildParameter(
          'name',
          'Item name',
          'Basic information',
          items,
          item => item.description1
        ),
      ],
    };
  }

  private buildClassificationGroup(items: ToolItem[]): ComparisonRowGroup {
    return {
      groupName: 'Classification',
      parameters: [
        this.buildParameter(
          'toolType',
          'Tool type',
          'Classification',
          items,
          item => this.getClassification(item)?.toolClassType?.description1
        ),
        this.buildParameter(
          'toolClass',
          'Tool class',
          'Classification',
          items,
          item => this.getClassification(item)?.toolClass?.description1
        ),
        this.buildParameter(
          'toolGroup',
          'Tool group',
          'Classification',
          items,
          item => this.getClassification(item)?.toolGroup?.description1
        ),
      ],
    };
  }

  private buildSupplierGroup(items: ToolItem[]): ComparisonRowGroup {
    return {
      groupName: 'Supplier information',
      parameters: [
        this.buildParameter(
          'supplier',
          'Manufacturer',
          'Supplier information',
          items,
          item => item.suppliers?.[0]?.supplierId
        ),
        this.buildParameter(
          'orderNumber',
          'Order code',
          'Supplier information',
          items,
          item => item.suppliers?.[0]?.orderNumber
        ),
      ],
    };
  }

  private buildTechnicalParametersGroup(items: ToolItem[]): ComparisonRowGroup {
    const allParameterDisplayIds = this.collectAllParameterDisplayIds(items);

    return {
      groupName: 'Technical parameters',
      parameters: Array.from(allParameterDisplayIds).map(displayId => {
        const { displayName, description } = this.findParameterInfo(items, displayId);
        return {
          name: displayId,
          displayName,
          group: 'Technical parameters',
          description,
          values: this.buildTechnicalValuesMap(items, displayId),
        };
      }),
    };
  }

  private buildParameter(
    name: string,
    displayName: string,
    group: ComparisonGroupName,
    items: ToolItem[],
    valueExtractor: (item: ToolItem) => string | number | boolean | null | undefined
  ): ComparisonParameter {
    return {
      name,
      displayName,
      group,
      values: this.buildValuesMap(items, valueExtractor),
    };
  }

  private buildValuesMap<T>(
    items: ToolItem[],
    valueExtractor: (item: ToolItem) => T
  ): Record<string, NonNullable<T> | null> {
    return items.reduce(
      (acc, item) => {
        if (item.id) {
          acc[item.id] = valueExtractor(item) ?? null;
        }
        return acc;
      },
      {} as Record<string, NonNullable<T> | null>
    );
  }

  private buildTechnicalValuesMap(items: ToolItem[], displayId: string): Record<string, string> {
    return items.reduce(
      (valuesAccumulator, item) => {
        if (item.id) {
          const matchingParameter = this.getItemParameters(item).find(
            parameter => parameter.displayId === displayId
          );
          valuesAccumulator[item.id] = matchingParameter
            ? this.formatTechnicalValue(matchingParameter, matchingParameter.unit?.description)
            : 'N/A';
        }
        return valuesAccumulator;
      },
      {} as Record<string, string>
    );
  }

  private formatTechnicalValue(parameter: TechnicalParameter, metricUnitSymbol?: string): string {
    const parameterValue = parameter.value;
    if (!parameterValue) return 'N/A';

    const valueType = parameter.valueDefinition?.valueType;
    const decimalPrecision = parameter.valueDefinition?.precision ?? 3;

    switch (valueType) {
      case ToolDataModuleDataContractsDeclaredValueType.Boolean:
        return parameterValue.booleanValue !== null && parameterValue.booleanValue !== undefined
          ? parameterValue.booleanValue
            ? 'Yes'
            : 'No'
          : 'N/A';

      case ToolDataModuleDataContractsDeclaredValueType.String:
        return parameterValue.stringValue?.toString().trim() || 'N/A';

      case ToolDataModuleDataContractsDeclaredValueType.Integer: {
        if (parameterValue.integerValue === null || parameterValue.integerValue === undefined)
          return 'N/A';
        const predefinedValueMatch = parameter.predefinedValues?.find(
          predefinedValue => predefinedValue.value?.integerValue === parameterValue.integerValue
        );
        if (predefinedValueMatch?.description) return predefinedValueMatch.description;
        const formattedIntegerValue = parameterValue.integerValue.toString();
        return metricUnitSymbol && metricUnitSymbol !== 'N/A'
          ? `${formattedIntegerValue} ${metricUnitSymbol}`
          : formattedIntegerValue;
      }

      case ToolDataModuleDataContractsDeclaredValueType.Decimal: {
        if (parameterValue.decimalValue === null || parameterValue.decimalValue === undefined)
          return 'N/A';
        const formattedDecimalValue = parameterValue.decimalValue.toFixed(decimalPrecision);
        return metricUnitSymbol && metricUnitSymbol !== 'N/A'
          ? `${formattedDecimalValue} ${metricUnitSymbol}`
          : formattedDecimalValue;
      }

      default:
        return 'N/A';
    }
  }

  // ════════════════════════════════════════════════════════════════════════════════
  //  Helper Methods
  // ════════════════════════════════════════════════════════════════════════════════

  private isEmptySlot(id: string | null | undefined): boolean {
    return !id || id === '';
  }

  private countFilledSlots(itemIds: string[]): number {
    return itemIds.filter(id => !this.isEmptySlot(id)).length;
  }

  private padToMaxSlots(items: (ToolItem | null)[]): (ToolItem | null)[] {
    const padded = [...items];
    while (padded.length < this.maxItems) {
      padded.push(null);
    }
    return padded.slice(0, this.maxItems);
  }

  private getClassification(item: ToolItem): ClassificationDto | undefined {
    return item.classification as ClassificationDto | undefined;
  }

  private getItemParameters(item: ToolItem): TechnicalParameter[] {
    const classification = this.getClassification(item);
    return [...(classification?.parameters || []), ...(classification?.masterDataParameters || [])];
  }

  private collectAllParameterDisplayIds(items: ToolItem[]): Set<string> {
    const displayIds = new Set<string>();
    items.forEach(item => {
      this.getItemParameters(item).forEach(param => {
        if (param.displayId) displayIds.add(param.displayId);
      });
    });
    return displayIds;
  }

  private findParameterInfo(
    items: ToolItem[],
    displayId: string
  ): { displayName: string; description?: string } {
    for (const item of items) {
      const param = this.getItemParameters(item).find(p => p.displayId === displayId);
      if (param?.description1) {
        return { displayName: param.description1, description: param.description2 || undefined };
      }
    }
    return { displayName: displayId };
  }

  private removeItemWithoutPersisting(itemId: string): void {
    this.selectedItemIds.update(items => items.filter(id => id !== itemId));
  }

  private isHttpError(error: unknown): error is { status: number } {
    return typeof error === 'object' && error !== null && 'status' in error;
  }

  // ════════════════════════════════════════════════════════════════════════════════
  //  Storage/Persistence
  // ════════════════════════════════════════════════════════════════════════════════

  private writeStorage(ids: string[]): void {
    localStorage.setItem(this.storageKey, JSON.stringify(ids));
  }

  private saveToStorage(): void {
    this.writeStorage(this.selectedItemIds());
    this.idsChannel.post(this.selectedItemIds());
  }

  /**
   * Applies item ids received from another tab. Writes storage directly rather than via
   * saveToStorage() so the change is not re-broadcast (avoids an echo loop). Setting the
   * signal triggers the existing effect() that refetches the item data. Skips no-op updates
   * so identical ids don't trigger a needless refetch or a re-broadcast ping-pong.
   */
  private applyRemoteIds(ids: string[]): void {
    const current = this.selectedItemIds();
    if (current.length === ids.length && current.every((id, index) => id === ids[index])) return;

    this.writeStorage(ids);
    this.selectedItemIds.set(ids);
  }

  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        this.selectedItemIds.set(JSON.parse(stored) as string[]);
      }
    } catch (error) {
      console.warn('Failed to load comparison items from storage:', error);
      localStorage.removeItem(this.storageKey);
    }
  }
}
